/**
 * MermaidLightbox — fullscreen modal viewer with zoom + pan for an
 * already-rendered mermaid SVG.
 *
 * Used by `MermaidBlock` when the user clicks the expand icon.
 *
 * Zoom: mouse wheel (anchored at cursor, 0.2× – 8×) or the `+` / `-`
 * buttons (anchored at canvas center). Pan: click-and-drag anywhere
 * on the canvas. Reset: double-click on the canvas or the reset
 * button. Close: `Esc`, click on the dim backdrop, or the close button.
 *
 * Scaling note: we scale the SVG via its container `width` percentage
 * (so the browser does a native SVG layout at the new size) instead of
 * a CSS `transform: scale()` on a wrapper. Mermaid emits node labels
 * inside `<foreignObject>` with HTML children; some browsers clip that
 * HTML content when its containing `<svg>` is CSS-transformed, even
 * though the SVG itself scales. Sizing via width % avoids the quirk —
 * the foreignObject re-measures HTML at the new size.
 */

import { RotateCcw, X, ZoomIn, ZoomOut } from "lucide-react";
import type { JSX, MouseEvent as ReactMouseEvent, WheelEvent as ReactWheelEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

const MIN_SCALE = 0.2;
const MAX_SCALE = 8;
const WHEEL_ZOOM_IN = 1.1;
const WHEEL_ZOOM_OUT = 1 / WHEEL_ZOOM_IN;
const BUTTON_ZOOM_STEP = 1.25;

interface MermaidLightboxProps {
  svg: string;
  onClose: () => void;
}

interface View {
  scale: number;
  x: number;
  y: number;
}

const INITIAL_VIEW: View = { scale: 1, x: 0, y: 0 };

interface DragState {
  startClientX: number;
  startClientY: number;
  startViewX: number;
  startViewY: number;
}

export function MermaidLightbox({ svg, onClose }: MermaidLightboxProps): JSX.Element {
  const [view, setView] = useState<View>(INITIAL_VIEW);
  const [dragging, setDragging] = useState(false);
  const canvasRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState | null>(null);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => {
      window.removeEventListener("keydown", handleKey);
    };
  }, [onClose]);

  const reset = useCallback(() => setView(INITIAL_VIEW), []);

  /** Zoom anchored at (anchorX, anchorY) in canvas-local coords (top-left
   * origin) so the point under that screen position stays in place. */
  const zoomAt = useCallback((factor: number, anchorX: number, anchorY: number): void => {
    setView((prev) => {
      const nextScale = clamp(prev.scale * factor, MIN_SCALE, MAX_SCALE);
      if (nextScale === prev.scale) return prev;
      // Point in unscaled wrapper coords (origin at wrapper's top-left,
      // which sits at (prev.x, prev.y) on the canvas):
      const wrapperLocalX = (anchorX - prev.x) / prev.scale;
      const wrapperLocalY = (anchorY - prev.y) / prev.scale;
      return {
        scale: nextScale,
        x: anchorX - wrapperLocalX * nextScale,
        y: anchorY - wrapperLocalY * nextScale,
      };
    });
  }, []);

  const zoomFromCenter = useCallback(
    (factor: number) => {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;
      zoomAt(factor, rect.width / 2, rect.height / 2);
    },
    [zoomAt],
  );

  const handleWheel = (e: ReactWheelEvent<HTMLDivElement>): void => {
    const rect = e.currentTarget.getBoundingClientRect();
    const cursorX = e.clientX - rect.left;
    const cursorY = e.clientY - rect.top;
    const factor = e.deltaY < 0 ? WHEEL_ZOOM_IN : WHEEL_ZOOM_OUT;
    zoomAt(factor, cursorX, cursorY);
  };

  const handleMouseDown = (e: ReactMouseEvent<HTMLDivElement>): void => {
    dragRef.current = {
      startClientX: e.clientX,
      startClientY: e.clientY,
      startViewX: view.x,
      startViewY: view.y,
    };
    setDragging(true);
  };

  const handleMouseMove = (e: ReactMouseEvent<HTMLDivElement>): void => {
    const drag = dragRef.current;
    if (!drag) return;
    setView((prev) => ({
      ...prev,
      x: drag.startViewX + (e.clientX - drag.startClientX),
      y: drag.startViewY + (e.clientY - drag.startClientY),
    }));
  };

  const endDrag = (): void => {
    dragRef.current = null;
    setDragging(false);
  };

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: ESC handler is registered at the window level (see useEffect above) — covering all keyboard users; this div's onClick is the mouse-only "click backdrop to close" convention shared with Radix-style modals.
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={onClose}
      // biome-ignore lint/a11y/useSemanticElements: the native <dialog> element forces .showModal() lifecycle + scoped CSS that conflicts with our z-index / backdrop styling; <div role="dialog" aria-modal> is the standard pattern used by Radix UI Dialog in this repo.
      role="dialog"
      aria-modal="true"
      aria-label="Visor de diagrama"
    >
      {/* Canvas: catches wheel + drag; clicks bubble-stop so the
          backdrop's onClick doesn't close the modal mid-drag. */}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: onClick here only stops propagation to the backdrop; canvas interactions are pointer-driven (wheel/drag) — keyboard users close via ESC (window-level handler above). */}
      <div
        ref={canvasRef}
        className="absolute inset-4 overflow-hidden rounded-lg border border-border bg-background"
        onClick={(e) => e.stopPropagation()}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
        onDoubleClick={reset}
        style={{ cursor: dragging ? "grabbing" : "grab" }}
      >
        {/* oxlint-disable react-doctor/no-danger -- trusted SVG output from mermaid (see biome-ignore on the dangerouslySetInnerHTML line) */}
        <div
          // Wrapper: SVG fills 100% of this div (mermaid emits width="100%");
          // we control the rendered size via the wrapper's `width` CSS in
          // percentage of the canvas. Pan is `translate(...)`. Native
          // SVG layout at the new size avoids the foreignObject-clipping
          // browser quirk caused by CSS `transform: scale()`.
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: `${view.scale * 100}%`,
            transform: `translate(${view.x}px, ${view.y}px)`,
            transformOrigin: "0 0",
            transition: dragging ? "none" : "transform 80ms, width 80ms",
          }}
          // mermaid.render output is trusted (securityLevel: 'strict' on
          // init); the same svg string is already rendered inline by
          // MermaidBlock — see that file for the security note.
          // biome-ignore lint/security/noDangerouslySetInnerHtml: trusted SVG output from mermaid
          dangerouslySetInnerHTML={{ __html: svg }}
        />
        {/* oxlint-enable react-doctor/no-danger */}
      </div>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: onClick here only stops bubble to the backdrop; the buttons inside are real <button> elements with their own keyboard activation. */}
      <div
        className="absolute right-6 top-6 flex items-center gap-1 rounded-lg border border-border bg-background p-1 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => zoomFromCenter(BUTTON_ZOOM_STEP)}
          className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          title="Acercar"
          aria-label="Acercar"
        >
          <ZoomIn className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => zoomFromCenter(1 / BUTTON_ZOOM_STEP)}
          className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          title="Alejar"
          aria-label="Alejar"
        >
          <ZoomOut className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={reset}
          className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          title="Reset"
          aria-label="Reset"
        >
          <RotateCcw className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          title="Cerrar (Esc)"
          aria-label="Cerrar"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="pointer-events-none absolute bottom-6 left-1/2 -translate-x-1/2 rounded-full bg-background/80 px-3 py-1 text-body-3 text-muted-foreground">
        wheel para zoom · drag para pan · doble click para reset · esc para cerrar ·{" "}
        {Math.round(view.scale * 100)}%
      </div>
    </div>
  );
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
